import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import useDatePickerContext from '@/hooks/useDatePickerContext';
import useVillage from '@/hooks/useVillage';
import useVillageContext from '@/hooks/useVillageContext';
import { cn } from '@/lib/utils';
import { Village } from '@/services/types';
import { translateWithParams } from '@/utils/translateWithParams';
import { useEffect, useState } from 'react';

type Props = {
  dict: IDatePickerDictonary;
  className?: string;
  village?: Village;
  selectedDates: {
    start: Date | null;
    end: Date | null;
  };
};

type CheckInErrorMessageProps = {
  checkInDays: string[] | undefined;
  dict: IDatePickerDictonary;
  calendarErrorTypes: string[];
};

export default function DatePickerError({
  dict,
  className,
  selectedDates,
  village,
}: Props) {
  const [periodMinNights, setPeriodMinNights] = useState(1);
  const [nightsToCharge, setNightsToCharge] = useState(7);
  const [minDaysErrorText, setMinDaysErrorText] = useState<
    'multipleDays' | 'minDays'
  >('minDays');
  const { errors } = dict;
  const { getMinNights, countNights, getNightsToCharge, isWeeklyBooking } =
    useVillage({});

  const {
    calendarErrorTypes,
    handleCalendarErrorType,
    clearCalendarErrorType,
    calendarHasErrorType,
    checkInDays,
  } = useDatePickerContext();

  const { calendarPromotionTitle } = useVillageContext();

  useEffect(() => {
    const rangeIsSelected = selectedDates.start && selectedDates.end;
    if (calendarHasErrorType('maxNights')) {
      clearCalendarErrorType('maxNights');
    }

    if (village && rangeIsSelected) {
      const nightsCount = countNights(selectedDates);
      const minNights = getMinNights({ selectedDates, village, nightsCount });
      const weeklyBooking = isWeeklyBooking({
        village,
        selectedDates,
        nightsCount,
      });
      const nightsToCharge = getNightsToCharge(
        weeklyBooking,
        nightsCount,
        minNights,
      );

      const hasMinDays = nightsCount >= nightsToCharge;

      if (!hasMinDays) {
        handleCalendarErrorType('minDays');

        setPeriodMinNights(minNights);
        setNightsToCharge(nightsToCharge);

        setMinDaysErrorText(weeklyBooking ? 'multipleDays' : 'minDays');
      } else {
        if (calendarHasErrorType('minDays')) {
          clearCalendarErrorType('minDays');
        }
      }
    }
  }, [selectedDates]);

  return (
    <div
      className={cn('text-sm md:text-base relative w-full', className, {
        'min-h-[55px]': !calendarPromotionTitle,
      })}>
      {calendarErrorTypes.includes('datesNoLongerAvailable') && (
        <p className="text-white bg-datepicker-error text-center py-[5px] px-[15px]">
          {errors.datesNoLongerAvailable}
        </p>
      )}
      {calendarErrorTypes.includes('maxNights') && (
        <p className="text-white bg-datepicker-error text-center py-[5px] px-[15px]">
          {errors.maxNights}
        </p>
      )}
      {calendarErrorTypes.includes('minDays') && (
        <p className="text-white bg-datepicker-error text-center py-[5px] px-[15px]">
          {translateWithParams(errors[minDaysErrorText], {
            minNights: periodMinNights,
            nightsToCharge,
          })}
        </p>
      )}
      <CheckInErrorMessage
        dict={dict}
        checkInDays={checkInDays}
        calendarErrorTypes={calendarErrorTypes}
      />
    </div>
  );
}

function CheckInErrorMessage({
  checkInDays,
  dict,
  calendarErrorTypes,
}: CheckInErrorMessageProps) {
  const { errors, weekDays } = dict;

  function replaceDays(template: string, ...values: string[]) {
    return template.replace(/%\w+%/g, function () {
      return values.shift() || '';
    });
  }

  if (!checkInDays || !Array.isArray(checkInDays)) {
    return null;
  }

  let message = '';

  const errorMessages: { [key: string]: () => string } = {
    selectNextAvailableDay: () => {
      const dayLabel = weekDays[checkInDays[0]];
      return replaceDays(errors.nextAvailableDay, dayLabel);
    },
    selectOneOfTwoAvailableDay: () => {
      const [firstDayLabel, secondDayLabel] = checkInDays.map(
        (day) => weekDays[day],
      );
      return replaceDays(
        errors.nextTwoAvailableDay,
        firstDayLabel,
        secondDayLabel,
      );
    },
    selectOneOfAvailableDays: () => {
      const lastDayIndex = checkInDays.pop() || '';
      const lastDayLabel = weekDays[lastDayIndex];
      const otherDaysLabel = checkInDays.map((day) => weekDays[day]).join(', ');
      return replaceDays(errors.nextAvilableDays, otherDaysLabel, lastDayLabel);
    },
  };

  for (let errorType of calendarErrorTypes) {
    if (errorMessages[errorType]) {
      message = errorMessages[errorType]();
      break;
    }
  }

  if (message) {
    return (
      <p className="text-white bg-datepicker-error text-center py-[5px] px-[15px] mt-[5px]">
        {message}
      </p>
    );
  }

  return null;
}
